import AddressInput from '@/gf/components/AddressInput'
import Button from '@/gf/components/ButtonOld'
import Card from '@/gf/components/Card'
import Checkbox from '@/gf/components/Checkbox'
import ConfirmationModal from '@/gf/components/ConfirmationModal'
import Field from '@/gf/components/Field'
import Label from '@/gf/components/Label'
import Layout from '@/gf/components/Layout'
import { InStockPill, LeadTimePill } from '@/gf/components/LineItem'
import LinkButton from '@/gf/components/LinkButtonOld'
import ModalForm from '@/gf/components/ModalForm'
import Page from '@/gf/components/Page'
import PickupField from '@/gf/components/PickupField'
import PriceField from '@/gf/components/PriceField'
import SpinnerSmall from '@/gf/components/SpinnerSmall'
import TextArea from '@/gf/components/TextArea'
import TextField from '@/gf/components/TextField'
import Tooltip from '@/gf/components/Tooltip'
import DateInput from '@/gf/components/inputs/Date'
import PriceInput from '@/gf/components/inputs/Price'
import useAddAltMpn from '@/gf/hooks/useAddAltMpn'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import MoneyM from '@/gf/modules/Money'
import ProductM from '@/gf/modules/Product'
import TimeM from '@/gf/modules/Time'
import UserContext from '@/gf/modules/User'
import { UNKNOWN_ERROR } from '@/gf/providers/MsgsProvider'
import * as api from '@/suppliers/modules/api'
import { StoreOrdersSearch } from '@/suppliers/modules/api'
import classNames from 'classnames'
import { GraphQLError } from 'graphql'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import { DateTime } from 'luxon'
import pluralize from 'pluralize'
import { Fragment, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import {
  Address,
  EditLineItem,
  EditLineItemChanges,
  GqlError,
  LineItem,
  LineItemInput,
  ProductSearchResult,
} from '../../types'
import AddProductFilters from '../components/AddProductFilters'
import EmailActionModal from '../components/EmailActionModal'
import Frame from '../components/Frame'
import UpdateConvenienceFeeModal from '../components/UpdateConvenienceFeeModal'
import useSession from '../hooks/useSession'
import useUpdateStoreOrder from '../hooks/useUpdateStoreOrder'
import StoreOrderM from '../modules/StoreOrder'

interface EditOrderForm {
  pickupAddress: Address | null
}

// `changes` is keyed by `EditLineItem.id` — the server-side OLI id for
// existing rows, or a client-minted UUID (see `addProduct`) for newly added
// rows. Pre-3077 this used `product.id`, which silently dropped the second
// edit when an order legitimately had two OLIs sharing the same product
// (CAT/Cummins-style multi-sub-assembly quotes).
const getUpdatedOrderLineItems = (
  orderLineItems: LineItem[],
  changes: EditLineItemChanges
): EditLineItem[] => {
  const existingIds = new Set(orderLineItems.map((li) => li.id))

  const addedLineItems = Object.keys(changes)
    .filter((key) => !existingIds.has(key))
    .map((key) => changes[key])

  const existingLineItems = orderLineItems.map((orderLineItem) => {
    const change = changes[orderLineItem.id]
    const result: EditLineItem = {
      ...orderLineItem,
      inStock: change?.inStock ?? orderLineItem.inStock,
      availableAt: change?.availableAt ?? orderLineItem.availableAt,
      quantity: change?.quantity ?? orderLineItem.quantity,
      unitPrice: change?.unitPrice ?? orderLineItem.unitPrice,
      discountedUnitPrice: change?.discountedUnitPrice ?? orderLineItem.discountedUnitPrice,
      externalId: orderLineItem.externalId,
      taskNumber: orderLineItem.taskNumber,
      suggestion: orderLineItem.suggestion,
      extendedPrice: change?.extendedPrice ?? orderLineItem.extendedPrice,
    }
    return result
  })

  return [...addedLineItems, ...existingLineItems]
}

const toErrorMsg = (gqlError: GqlError) => {
  const addressMsg =
    'Pickup Address must have: First name, Last name, Country, City, Street address, and State'

  return {
    pickup_address_required: addressMsg,
    pickup_address_city_required: addressMsg,
    pickup_address_country_required: addressMsg,
    pickup_address_line_one_required: addressMsg,
    pickup_address_state_required: addressMsg,
  }[gqlError.message]
}

const OrderLineItem = ({
  pickup,
  changedItem,
  originalItem,
  onAdjustAvailability,
  onAdjustQuantity,
  onAdjustPrice,
  onRemove,
  onEditAltMpn,
}: {
  pickup: boolean
  changedItem: EditLineItem
  originalItem: LineItem | undefined
  onAdjustAvailability: () => void
  onAdjustQuantity: () => void
  onAdjustPrice: () => void
  onRemove: () => void
  onEditAltMpn: () => void
}) => {
  const { user } = useSession()

  return (
    <div className="flex space-x-4">
      <img
        src={ProductM.primaryImageURL(changedItem.product, 'card')}
        alt={changedItem.product.name}
        className="w-16 h-16 border rounded-sm overflow-hidden text-xs"
      />
      <div className="flex flex-1 flex-col">
        <p className="text-sm text-gray-900 font-bold">{changedItem.product.name}</p>
        <p className="text-sm text-gray-500">{`MPN: ${changedItem.product.mpn}`}</p>
        {changedItem.inStock ? (
          <InStockPill className="self-start" />
        ) : changedItem.inStock !== null && changedItem.availableAt ? (
          <div className="flex flex-row justify-start items-center space-x-1 sm:space-x-1.5">
            <LeadTimePill availableAt={changedItem.availableAt} />
            <Tooltip
              text="This is the estimated date shared with the customer to indicate when the part will be ready"
              size="medium"
            />
          </div>
        ) : null}
        {UserContext.isAdmin(user) && changedItem.product.altMpn ? (
          <p className="text-sm text-gray-500">{`Alt MPN: ${changedItem.product.altMpn}`}</p>
        ) : (
          <></>
        )}
        {!originalItem && (
          <ul className="list-disc list-inside mt-1">
            <li className="text-sm text-yellow-500">Added to order</li>
          </ul>
        )}
        <div className="flex w-full mt-2 text-sm">
          <div className="flex space-x-4 w-full w-3/4">
            <LinkButton type="button" onClick={onAdjustQuantity}>
              Adjust Quantity
            </LinkButton>
            <LinkButton type="button" onClick={onAdjustPrice}>
              Adjust Price
            </LinkButton>
            {changedItem.quantity !== 0 && (
              <LinkButton type="button" onClick={onRemove}>
                Remove Item
              </LinkButton>
            )}

            {UserContext.isAdmin(user) ? (
              <LinkButton type="button" onClick={onEditAltMpn}>
                Edit Alt Mpn
              </LinkButton>
            ) : (
              <></>
            )}
          </div>
          <div className="flex w-full w-1/4 justify-end">
            <Checkbox
              checked={!!changedItem.inStock}
              onChange={onAdjustAvailability}
              className="whitespace-nowrap"
              color="blue"
            >
              <span className="text-sm text-gray-700">
                Available {!pickup ? 'to' : 'for'}{' '}
                <span className="text-gray-900 font-medium">{pickup ? 'will call' : 'ship'}</span>{' '}
                today
              </span>
            </Checkbox>
          </div>
        </div>
      </div>
      <div className="text-sm text-gray-700 text-right">
        {originalItem &&
          (changedItem.quantity !== originalItem.quantity ||
            !MoneyM.equals(changedItem.unitPrice, originalItem.unitPrice)) && (
            <div className="flex gap-4 text-gray-500">
              <div className="flex-1" />
              <div>
                <span className="space-x-2">
                  {!MoneyM.equals(originalItem.unitPrice, originalItem.discountedUnitPrice) && (
                    <span className="line-through text-gray-500">
                      {MoneyM.format(originalItem.unitPrice)}
                    </span>
                  )}
                  <span
                    className={classNames({
                      'line-through': !MoneyM.equals(changedItem.unitPrice, originalItem.unitPrice),
                    })}
                  >
                    {MoneyM.format(originalItem.discountedUnitPrice)}
                  </span>
                </span>{' '}
                x{' '}
                <span
                  className={classNames({
                    'line-through': changedItem.quantity !== originalItem.quantity,
                  })}
                >
                  {originalItem.quantity}
                </span>
              </div>
              <div className="line-through">{MoneyM.format(originalItem.extendedPrice)}</div>
            </div>
          )}
        <div className="flex gap-4">
          <div className="flex-1" />
          <div>
            <span className="space-x-2">
              {!MoneyM.equals(changedItem.unitPrice, changedItem.discountedUnitPrice) && (
                <span className="line-through text-gray-500">
                  {MoneyM.format(changedItem.unitPrice)}
                </span>
              )}
              <span>{MoneyM.format(changedItem.discountedUnitPrice)}</span>
            </span>{' '}
            x {changedItem.quantity}
          </div>
          <div>{MoneyM.format(changedItem.extendedPrice)}</div>
        </div>
      </div>
    </div>
  )
}

const EditOrder = () => {
  const { storeOrderId } = useParams<{ storeOrderId: string }>() as { storeOrderId: string }
  const navigate = useNavigate()
  const { user, store } = useSession()
  const [_msgs, msgsMgr] = useMsgs()
  const [changes, setChanges] = useState<EditLineItemChanges>({})
  const [adjustQuantityModalOpen, adjustQuantityModalToggler] = useToggle()
  const [updateConvenienceModalOpen, updateConvenienceModalToggler] = useToggle()
  const [availabilityModalOpen, availabilityModalToggler] = useToggle()
  const [adjustUnitPriceModalOpen, adjustUnitPriceModalToggler] = useToggle()
  const [shippingModalOpen, shippingModalToggler] = useToggle()
  const [addAltMpnModalOpen, addAltMpnModalToggler] = useToggle()
  const [altMpn, setAltMpn] = useState<string>('')
  const addAltMpn = useAddAltMpn()
  const [storeOrder, setStoreOrder] = useState<StoreOrdersSearch['storeOrders'][number] | null>()
  const [activeLineItem, setActiveLineItem] = useState<EditLineItem>()
  const [availableAt, setAvailableAt] = useState<DateTime | undefined>()
  const [quantityText, setQuantityText] = useState<string>('')
  const [unitPriceText, setUnitPriceText] = useState<string>('')
  const [productsFromQuery, setProductsFromQuery] = useState<ProductSearchResult[]>([])
  const [editEmailAdditionalCommentsText, setEditEmailAdditionalCommentsText] = useState('')
  const [editModalOpen, editModalToggler] = useToggle()
  const [updatedShipping, setUpdatedShipping] = useState(MoneyM.fromDecimal(0, 'USD'))
  const [shippingText, setShippingText] = useState('')
  const [updatedTax, setUpdatedTax] = useState(MoneyM.fromDecimal(0, 'USD'))
  const [taxText, setTaxText] = useState('')
  const [taxModalOpen, taxModalToggler] = useToggle()
  const [discountModalOpen, discountModalToggler] = useToggle()
  const [updatedDiscountPercent, setUpdatedDiscountPercent] = useState<number | null>(null)
  const [discountText, setDiscountText] = useState('')
  const [spinnerLive, spinner] = useToggle()
  const [editOrderEmailCheckbox, setEditOrderEmailCheckbox] = useState<boolean>(true)
  const [customProductModalOpen, customProductModalToggler] = useToggle()
  const [customProductName, setCustomProductName] = useState<string>('')
  const [customProductPrice, setCustomProductPrice] = useState<string>('')
  const [customProductMPN, setCustomProductMPN] = useState<string>('')
  const [shippingLoading, toggleShippingLoading] = useToggle()
  const [shippingLeadTime, setShippingLeadTime] = useState('')
  const [pickup, setPickup] = useState(false)
  const [form, setForm] = useState<EditOrderForm>({ pickupAddress: null })
  const updateStoreOrder = useUpdateStoreOrder()
  const [pendingRemoveItem, setPendingRemoveItem] = useState<EditLineItem | null>(null)

  const breadcrumbs = {
    copy: 'Back to Dashboard',
    crumbs: [
      { name: 'Orders', href: '/orders' },
      { name: 'Order details', href: `/orders/${storeOrderId}` },
      { name: 'Edit order', href: `/orders/${storeOrderId}/edit` },
    ],
  }

  useEffect(() => {
    api.fetchStoreOrder(storeOrderId).then((so) => {
      setStoreOrder(so)

      if (so) {
        setUpdatedShipping(so.shippingAmount)
        setUpdatedTax(so.salesTax)
        setShippingLeadTime(so.timingDetails)
        setPickup(so.pickup)

        const pickupAddress = omit(so.pickupAddress, ['__typename']) as Address
        setForm({ pickupAddress })

        // Initialize discount from the first discount (if any)
        const initialDiscount = so.discounts?.[0]?.percent ?? null
        setUpdatedDiscountPercent(initialDiscount)
      }
    })
  }, [storeOrderId])

  const shippingHasChanged =
    storeOrder && !MoneyM.equals(updatedShipping, storeOrder.shippingAmount)
  const taxHasChanged = storeOrder && !MoneyM.equals(updatedTax, storeOrder.salesTax)
  const timingDetailsChanged = storeOrder && shippingLeadTime !== storeOrder.timingDetails
  const pickupChanged = storeOrder && pickup !== storeOrder.pickup
  const pickupAddressChanged =
    storeOrder && !isEqual(omit(storeOrder.pickupAddress, ['__typename']), form.pickupAddress)
  const originalDiscountPercent = storeOrder?.discounts?.[0]?.percent ?? null
  const discountHasChanged = updatedDiscountPercent !== originalDiscountPercent

  const storeOrderHasChanged =
    Object.keys(changes).length !== 0 ||
    shippingHasChanged ||
    taxHasChanged ||
    timingDetailsChanged ||
    pickupChanged ||
    pickupAddressChanged ||
    discountHasChanged

  // Prompt the user to commit staged changes before exiting the page
  useEffect(() => {
    const alertUser = (e: BeforeUnloadEvent) => {
      if (storeOrderHasChanged) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', alertUser)
    return () => {
      window.removeEventListener('beforeunload', alertUser)
    }
  }, [storeOrderHasChanged])

  // Merge the changes and order line items to get the updated order line items
  const updatedOrderLineItems = getUpdatedOrderLineItems(
    storeOrder ? storeOrder.lineItems : [],
    changes
  )

  const calculateAndUpdateShipping = () => {
    if (storeOrder) {
      toggleShippingLoading.on()
      const lineItemInputs: LineItemInput[] = updatedOrderLineItems.map((lineItem) => ({
        inStock: lineItem.inStock,
        availableAt: lineItem.availableAt,
        productId: lineItem.product.id,
        quantity: lineItem.quantity,
        unitPrice: MoneyM.toDecimal(lineItem.unitPrice),
      }))
      api
        .calculateShippingCost(storeOrder.store.id, lineItemInputs)
        .then((shippingCost) => setUpdatedShipping(shippingCost))
        .catch((_reason) => {
          msgsMgr.add('Failed to calculate new shipping cost. Please contact support.', 'negative')
        })
        .finally(() => toggleShippingLoading.off())
    }
  }

  useEffect(() => {
    if (storeOrder && storeOrder.gearflowShipping && !pickup) {
      calculateAndUpdateShipping()
    }
  }, [changes, pickup])

  // Recalculate all line item prices when discount changes
  useEffect(() => {
    if (!storeOrder || updatedDiscountPercent === originalDiscountPercent) return

    const updatedDiscount =
      updatedDiscountPercent !== null ? [{ percent: updatedDiscountPercent }] : []

    // Recalculate prices for all line items (both existing and changed)
    const newChanges: EditLineItemChanges = {}

    // Process all line items (existing + any already changed ones)
    const allLineItems = getUpdatedOrderLineItems(storeOrder.lineItems, changes)

    allLineItems.forEach((lineItem) => {
      if (!lineItem.id) return

      const discountedUnitPrice = StoreOrderM.getDiscountedUnitPriceDecimal(
        { discounts: updatedDiscount },
        MoneyM.toDecimal(lineItem.unitPrice)
      )
      const extendedPrice = discountedUnitPrice * lineItem.quantity

      newChanges[lineItem.id] = {
        ...lineItem,
        discountedUnitPrice: MoneyM.fromDecimal(
          discountedUnitPrice,
          lineItem.unitPrice.currency.code
        ),
        extendedPrice: MoneyM.fromDecimal(extendedPrice, lineItem.unitPrice.currency.code),
      }
    })

    setChanges(newChanges)
  }, [updatedDiscountPercent])

  // Return a loading page when the storeOrder is undefined
  if (storeOrder === undefined) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Loading..." />
      </Frame>
    )
  }

  // Not found or not authorized
  // TODO: move auth check to backend
  if (!storeOrder || (!UserContext.isAdmin(user) && storeOrder.store.id !== store.id)) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p>Cannot find that order.</p>
              <p>
                <Link className="text-blue-500 underline" to="/orders">
                  Back to all orders
                </Link>
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      </Frame>
    )
  }

  // Create a virtual storeOrder with updated discounts for price calculations
  const storeOrderWithUpdatedDiscount = {
    ...storeOrder,
    discounts: updatedDiscountPercent !== null ? [{ percent: updatedDiscountPercent }] : [],
  }

  const subtotal = StoreOrderM.subtotal(storeOrder)
  const additionalCharges = storeOrder.additionalCharges.map((ac) => ac.price)

  const totalWithoutConvenienceFee = [
    subtotal,
    storeOrder.shippingAmount,
    storeOrder.salesTax,
    ...additionalCharges,
  ].reduce(MoneyM.add)

  const convenienceFee = MoneyM.mult(totalWithoutConvenienceFee, storeOrder.convenienceFeeRate)
  const total = MoneyM.add(totalWithoutConvenienceFee, convenienceFee)

  // The count isn't always the length of the list, because a removed line item exists in the list with quantity 0
  const updatedOrderLineItemsCount = updatedOrderLineItems.reduce(
    (count, lineItem) => (lineItem.quantity === 0 ? count : count + 1),
    0
  )

  const updatedSubtotal = updatedOrderLineItems
    .map((li) => li.extendedPrice)
    .reduce(MoneyM.add, MoneyM.fromDecimal(0, 'USD'))

  const updatedTotalWithoutConvenienceFee = [
    updatedSubtotal,
    updatedShipping,
    updatedTax,
    ...additionalCharges,
  ].reduce(MoneyM.add)

  const updatedConvenienceFee = MoneyM.mult(
    updatedTotalWithoutConvenienceFee,
    storeOrder.convenienceFeeRate
  )

  const updatedTotal = MoneyM.add(updatedTotalWithoutConvenienceFee, updatedConvenienceFee)

  // Determine if we're in a pre-approval state (state will change to open_quote on edit)
  const isExternalOrder = storeOrder.externalOrder
  const isPreApprovalState = ['RECEIVED', 'OPEN_QUOTE', 'QUOTE_DENIED'].includes(storeOrder.state)

  // State will change if: pre-approval state AND not an external order (Log Orders never change state)
  const willChangeState = isPreApprovalState && !isExternalOrder

  // Show warning only if state will actually change (not already open_quote)
  const willResetToOpenQuote = willChangeState && storeOrder.state !== 'OPEN_QUOTE'

  const removeChangeForLineItem = (lineItem: EditLineItem) => {
    if (!lineItem.id) return
    const newChanges = { ...changes }
    delete newChanges[lineItem.id]
    setChanges(newChanges)
  }

  // Helper to calculate shipped quantity from shipmentItems
  const getShippedQuantity = (item: EditLineItem): number => {
    const originalItem = storeOrder.lineItems.find((li) => li.id === item.id)
    if (!originalItem?.shipmentItems) return 0
    return originalItem.shipmentItems.reduce((sum, si) => sum + si.quantity, 0)
  }

  const performRemoveAction = (item: EditLineItem) => {
    if (item.id && storeOrder.lineItems.find((lineItem) => lineItem.id === item.id)) {
      setChanges({
        ...changes,
        [item.id]: {
          ...item,
          inStock: true,
          availableAt: null,
          quantity: 0,
          extendedPrice: MoneyM.fromDecimal(0, 'USD'),
        },
      })
    } else {
      // The removed item is not in the original order, so remove the change (since it is now undone)
      removeChangeForLineItem(item)
    }
    setPendingRemoveItem(null)
  }

  const removeAction = (item: EditLineItem) => {
    const shippedQuantity = getShippedQuantity(item)
    if (shippedQuantity > 0) {
      // Show confirmation modal for shipped items
      setPendingRemoveItem(item)
    } else {
      performRemoveAction(item)
    }
  }

  const adjustAvailabilityAction = () => {
    msgsMgr.clear()

    if (!activeLineItem) return

    if (availableAt) {
      const orderLineItem = storeOrder.lineItems.find(
        (lineItem) => lineItem.id === activeLineItem.id
      )

      const orderAvailability =
        orderLineItem && orderLineItem.availableAt?.toLocaleString(DateTime.DATE_SHORT)
      const selectedAvailability = availableAt.toLocaleString(DateTime.DATE_SHORT)

      if (orderLineItem && orderAvailability === selectedAvailability) {
        removeChangeForLineItem(activeLineItem)
      } else if (activeLineItem.id) {
        setChanges({
          ...changes,
          [activeLineItem.id]: {
            ...activeLineItem,
            availableAt,
            inStock: false,
          },
        })
      }

      setAvailableAt(undefined)
      availabilityModalToggler.off()
    } else {
      msgsMgr.add('Availability date is required', 'negative')
    }
  }

  const adjustQuantityAction = () => {
    if (!activeLineItem) return
    const quantity = quantityText === '' ? 0 : parseInt(quantityText, 10)
    if (quantity === 0) {
      removeAction(activeLineItem)
    } else {
      const orderLineItem = storeOrder.lineItems.find(
        (lineItem) => lineItem.id === activeLineItem.id
      )
      if (
        orderLineItem &&
        orderLineItem.quantity === quantity &&
        MoneyM.equals(orderLineItem.unitPrice, activeLineItem.unitPrice)
      ) {
        // Remove the existing change if the unit price also hasn't changed
        removeChangeForLineItem(activeLineItem)
      } else if (activeLineItem.id) {
        setChanges({
          ...changes,
          [activeLineItem.id]: {
            ...activeLineItem,
            quantity,
            extendedPrice: MoneyM.mult(activeLineItem.discountedUnitPrice, quantity),
          },
        })
      }
    }
    adjustQuantityModalToggler.off()
  }

  const adjustUnitPriceAction = () => {
    if (!activeLineItem) return
    const unitPrice = MoneyM.fromString(
      unitPriceText === '' ? '0' : unitPriceText,
      activeLineItem.unitPrice.currency.code
    )
    if (!MoneyM.equals(unitPrice, activeLineItem.unitPrice)) {
      const orderLineItem = storeOrder.lineItems.find(
        (lineItem) => lineItem.id === activeLineItem.id
      )
      if (
        orderLineItem &&
        MoneyM.equals(orderLineItem.unitPrice, unitPrice) &&
        orderLineItem.quantity === activeLineItem.quantity
      ) {
        // Remove the existing change if the quantity also hasn't changed
        removeChangeForLineItem(activeLineItem)
      } else if (activeLineItem.id) {
        const discountedUnitPrice = StoreOrderM.getDiscountedUnitPriceDecimal(
          storeOrderWithUpdatedDiscount,
          MoneyM.toDecimal(unitPrice)
        )
        const extendedPrice = discountedUnitPrice * activeLineItem.quantity
        // Update the change unit price
        setChanges({
          ...changes,
          [activeLineItem.id]: {
            ...activeLineItem,
            unitPrice,
            discountedUnitPrice: MoneyM.fromDecimal(discountedUnitPrice, unitPrice.currency.code),
            extendedPrice: MoneyM.fromDecimal(extendedPrice, unitPrice.currency.code),
          },
        })
      }
    }
    adjustUnitPriceModalToggler.off()
  }

  const updateShippingAction = () => {
    setUpdatedShipping(MoneyM.fromString(shippingText === '' ? '0' : shippingText, 'USD'))
    shippingModalToggler.off()
  }

  const addProduct = (product: ProductSearchResult) => {
    const existingLineItem = updatedOrderLineItems.find(
      (lineItem) => lineItem.product.id === product.id
    )

    const currencyCode = existingLineItem?.unitPrice.currency.code || 'USD'
    const quantity = existingLineItem ? existingLineItem.quantity + 1 : 1
    const discountedUnitPriceDecimal = StoreOrderM.getDiscountedUnitPriceDecimal(
      storeOrderWithUpdatedDiscount,
      product.salePrice
    )

    // For brand-new rows, mint a client-side UUID so the changes map has a
    // stable key per OLI even when two rows share the same product.id (the
    // synthetic id is filtered out at submit time — see updateOrder below).
    const id = existingLineItem?.id ?? uuid()

    const newLineItem: EditLineItem = {
      id,
      product,
      quantity,
      showMpn: true,
      externalId: null,
      taskNumber: null,
      suggestion: null,
      inStock: true,
      availableAt: null,
      unitPrice: MoneyM.fromDecimal(product.salePrice, currencyCode),
      discountedUnitPrice: MoneyM.fromDecimal(discountedUnitPriceDecimal, currencyCode),
      extendedPrice: MoneyM.fromDecimal(discountedUnitPriceDecimal * quantity, currencyCode),
    }

    const newChanges = {
      ...changes,
      [id]: newLineItem,
    }
    setChanges(newChanges)

    // Update the search results to show the correct items in the order
    const newUpdatedOrderLineItems = getUpdatedOrderLineItems(storeOrder.lineItems, newChanges)
    const idsInOrder = newUpdatedOrderLineItems.map((li) => li.product.id)
    const productSearchResults = productsFromQuery.map((p) => ({
      ...p,
      inOrder: idsInOrder?.includes(p.id) || false,
    }))
    setProductsFromQuery(productSearchResults)
  }

  const prepAndOpenAltMpnModal = (lineItem: EditLineItem) => {
    setActiveLineItem(lineItem)
    addAltMpnModalToggler.on()
  }

  const prepAndOpenAdjustQuantityModal = (lineItem: EditLineItem) => {
    setQuantityText(`${lineItem.quantity}`)
    setActiveLineItem(lineItem)
    adjustQuantityModalToggler.on()
  }

  const prepAndOpenAdjustUnitPriceModal = (lineItem: EditLineItem) => {
    setUnitPriceText(MoneyM.toString(lineItem.unitPrice))
    setActiveLineItem(lineItem)
    adjustUnitPriceModalToggler.on()
  }

  const prepAndOpenShippingModal = () => {
    setShippingText(`${MoneyM.toDecimal(updatedShipping)}`)
    shippingModalToggler.on()
  }

  const prepAndOpenTaxModal = () => {
    setTaxText(`${MoneyM.toDecimal(updatedTax)}`)
    taxModalToggler.on()
  }

  const updateTaxAction = () => {
    setUpdatedTax(MoneyM.fromString(taxText === '' ? '0' : taxText, 'USD'))
    taxModalToggler.off()
  }

  const prepAndOpenDiscountModal = () => {
    setDiscountText(updatedDiscountPercent !== null ? `${updatedDiscountPercent}` : '0')
    discountModalToggler.on()
  }

  const updateDiscountAction = () => {
    const newDiscount = discountText === '' ? 0 : parseFloat(discountText)
    // Treat 0 as no discount (null)
    setUpdatedDiscountPercent(newDiscount === 0 ? null : newDiscount)
    discountModalToggler.off()
  }

  const prepAndOpenAvailabilityModal = (lineItem: EditLineItem) => {
    if (lineItem.inStock) {
      setActiveLineItem(lineItem)
      availabilityModalToggler.on()
    } else {
      const orderLineItem = storeOrder.lineItems.find((li) => li.id === lineItem.id)

      if (orderLineItem && orderLineItem.inStock === !lineItem.inStock) {
        removeChangeForLineItem(lineItem)
      } else if (lineItem.id) {
        const newChanges = {
          ...changes,
          [lineItem.id]: {
            ...lineItem,
            availableAt: null,
            inStock: true,
          },
        }

        setChanges(newChanges)
      }
    }
  }

  const updateOrder = () => {
    spinner.on()

    // Strip client-minted UUIDs (added rows) from the payload — server
    // recognises an item as new when `id` is null.
    const existingIds = new Set(storeOrder.lineItems.map((li) => li.id))
    const lineItems = updatedOrderLineItems.map((li) => ({
      id: li.id && existingIds.has(li.id) ? li.id : null,
      productId: li.product.id,
      inStock: li.inStock,
      availableAt: li.inStock ? null : li.availableAt,
      unitPrice: MoneyM.toPayload(li.unitPrice),
      quantity: li.quantity,
      externalId: li.externalId,
      taskNumber: li.taskNumber,
      suggestion: li.suggestion,
    }))

    const pickupAddress = pickup ? form.pickupAddress : null

    updateStoreOrder({
      id: storeOrder.id,
      lineItems,
      shippingCost: MoneyM.toPayload(updatedShipping),
      taxCost: MoneyM.toPayload(updatedTax),
      sendEmail: willChangeState ? editOrderEmailCheckbox : false,
      notes: editEmailAdditionalCommentsText,
      pickup,
      pickupAddress,
      timingDetails: shippingLeadTime,
      discountPercent: updatedDiscountPercent,
    })
      .then(() => {
        const emailSent = willChangeState && editOrderEmailCheckbox
        msgsMgr.add(`Order updated.${emailSent ? ' Email sent to customer.' : ''}`, 'positive')
        navigate(`/orders/${storeOrderId}`)
      })
      .catch((err: { graphQLErrors: GqlError[] }) => {
        if (err.graphQLErrors.length > 0) {
          const error = [...new Set(err.graphQLErrors.map(toErrorMsg))].join(' / ')
          msgsMgr.add(error, 'negative')
        } else {
          msgsMgr.addUnknownError()
        }
      })

      .finally(() => {
        spinner.off()
        editModalToggler.off()
      })
  }

  const createAndAddCustomProduct = async () => {
    const response = await api.createOfflineProduct(
      customProductName,
      customProductPrice,
      customProductMPN,
      storeOrder.store.id
    )
    if (response.status !== 200) {
      const body = await response.text()
      msgsMgr.add(body, 'negative')
    } else {
      const productId = await response.text()
      // need to convert product -> productSearchResult type by adding inOrder boolean
      api.fetchProduct(productId).then((product) => addProduct({ ...product, inOrder: false }))
      setCustomProductName('')
      setCustomProductPrice('')
      setCustomProductMPN('')
    }
    customProductModalToggler.off()
  }

  const onUpdateButtonClicked = () => {
    // Show an error message if all of the line items have been removed
    if (updatedOrderLineItemsCount === 0) {
      msgsMgr.add('Order must have at least one item.', 'negative')
      return
    }
    editModalToggler.on()
  }

  // Use this instead of calling setPickup directly
  const onPickupChange = (checked: boolean) => {
    setPickup(checked)
    if (checked) {
      // Pickup orders must have $0 shipping
      setUpdatedShipping(MoneyM.fromDecimal(0, 'USD'))
    } else if (storeOrder.pickup) {
      // Calculate a table-rate shipping cost for the StoreOrder since it didn't have a shipping cost
      calculateAndUpdateShipping()
    } else {
      // Revert back to the StoreOrder's shipping cost
      setUpdatedShipping(storeOrder.shippingAmount)
    }
  }

  // Stay on the page: navigating away here (on save *and* on cancel) threw away every
  // unsaved line item edit, which is what made entered parts "reset" (GEA-2793).
  const onConvenienceFeeSaved = (convenienceFeeRate: number) =>
    setStoreOrder((current) => (current ? { ...current, convenienceFeeRate } : current))

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <EmailActionModal
        title="Update Order"
        open={editModalOpen}
        onClose={editModalToggler.off}
        onSubmit={updateOrder}
        isWaiting={spinnerLive}
        emailText={editEmailAdditionalCommentsText}
        setEmailText={setEditEmailAdditionalCommentsText}
        sendEmailCheckbox={editOrderEmailCheckbox}
        setSendEmailCheckbox={setEditOrderEmailCheckbox}
        hideNotificationOption={!willChangeState}
      >
        {willResetToOpenQuote && (
          <p className="text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
            <strong>Note:</strong> This order will be reset to &apos;Awaiting Approval&apos; status
            and will require customer re-approval.
          </p>
        )}
        {willChangeState ? (
          <p>Updating this order will notify the customer of the changes.</p>
        ) : (
          <p>Update the order details. No notification will be sent.</p>
        )}
      </EmailActionModal>
      <ConfirmationModal
        open={pendingRemoveItem !== null}
        onCancel={() => setPendingRemoveItem(null)}
        onConfirm={() => pendingRemoveItem && performRemoveAction(pendingRemoveItem)}
        title="Remove Shipped Item?"
        text="This item has already been partially or fully shipped. Removing it will also delete the shipment tracking records. Are you sure?"
      />
      <UpdateConvenienceFeeModal
        open={updateConvenienceModalOpen}
        onClose={updateConvenienceModalToggler.off}
        onSaved={onConvenienceFeeSaved}
        storeOrderId={storeOrder.id}
        convenienceFee={storeOrder.convenienceFeeRate}
      />
      <ModalForm
        open={availabilityModalOpen}
        onClose={availabilityModalToggler.off}
        title="Availability"
        submitButtonText="Done"
        onSubmit={(e) => {
          e.preventDefault()
          adjustAvailabilityAction()
        }}
      >
        <p className="mb-6">
          Adjust the availability for{' '}
          <span className="font-bold">{activeLineItem?.product.name}</span>.
        </p>
        <Label>Select Date</Label>
        <DateInput
          value={availableAt}
          min={TimeM.toDateInputFormat(DateTime.now())}
          onChange={(value) => {
            setAvailableAt(value)
          }}
        />
      </ModalForm>
      <ModalForm
        open={adjustQuantityModalOpen}
        onClose={adjustQuantityModalToggler.off}
        title="Adjust Quantity"
        submitButtonText="Done"
        onSubmit={(e) => {
          e.preventDefault()
          adjustQuantityAction()
        }}
      >
        <p className="mb-2">
          Adjust the quantity for <span className="font-bold">{activeLineItem?.product.name}</span>.
        </p>
        <Label>Quantity</Label>
        <input
          onChange={(e) => setQuantityText(e.target.value)}
          type="number"
          step={1}
          min={0}
          onKeyDown={() => false}
          value={quantityText}
          className="focus:ring-indigo-500 focus:border-indigo-500 block sm:text-sm border-gray-300 rounded-md w-full"
        />
      </ModalForm>
      <ModalForm
        open={adjustUnitPriceModalOpen}
        onClose={adjustUnitPriceModalToggler.off}
        title="Adjust Unit Price"
        submitButtonText="Done"
        onSubmit={(e) => {
          e.preventDefault()
          adjustUnitPriceAction()
        }}
      >
        <p className="mb-1">
          Adjust the unit price for{' '}
          <span className="font-bold">{activeLineItem?.product.name}</span>.
        </p>
        <p className="mb-2">
          <span className="font-semibold">Note:</span> This only changes the unit price on this
          order. This does NOT change the price of this product anywhere else on Gearflow.
        </p>
        <Label>Unit Price</Label>
        <PriceInput
          onChange={(e) => setUnitPriceText(e.target.value)}
          min={0}
          onKeyDown={() => false}
          value={unitPriceText}
        />
      </ModalForm>
      <ModalForm
        open={shippingModalOpen}
        onClose={shippingModalToggler.off}
        title="Adjust Shipping"
        submitButtonText="Done"
        onSubmit={(e) => {
          e.preventDefault()
          updateShippingAction()
        }}
      >
        <Label>Shipping</Label>
        <PriceInput
          onChange={(e) => setShippingText(e.target.value)}
          min={0}
          onKeyDown={() => false}
          value={shippingText}
        />
      </ModalForm>
      <ModalForm
        open={taxModalOpen}
        onClose={taxModalToggler.off}
        title="Adjust Tax"
        submitButtonText="Done"
        onSubmit={(e) => {
          e.preventDefault()
          updateTaxAction()
        }}
      >
        <Label>Sales Tax</Label>
        <PriceInput
          onChange={(e) => setTaxText(e.target.value)}
          min={0}
          onKeyDown={() => false}
          value={taxText}
        />
      </ModalForm>
      <ModalForm
        open={discountModalOpen}
        onClose={discountModalToggler.off}
        title="Adjust Discount"
        submitButtonText="Done"
        onSubmit={(e) => {
          e.preventDefault()
          updateDiscountAction()
        }}
      >
        <p className="mb-2 text-sm text-gray-600">
          Adjust the discount percentage applied to all line items. Set to 0 to remove the discount.
        </p>
        <Label>Discount Percentage (%)</Label>
        <input
          type="number"
          step="0.01"
          min={0}
          max={100}
          onChange={(e) => setDiscountText(e.target.value)}
          value={discountText}
          className="focus:ring-indigo-500 focus:border-indigo-500 block sm:text-sm border-gray-300 rounded-md w-full"
        />
      </ModalForm>
      <ModalForm
        open={customProductModalOpen}
        onClose={customProductModalToggler.off}
        title="Add Custom Product"
        onSubmit={(e) => {
          e.preventDefault()
          createAndAddCustomProduct()
        }}
        submitButtonText="Add"
      >
        <p className="text-sm text-gray-400 mb-2">
          Custom products are not public on gearflow.com.
        </p>
        <TextField
          label="Name"
          required
          onChange={(e) => setCustomProductName(e.target.value)}
          value={customProductName}
        />
        <PriceField
          label="Price"
          required
          onChange={(e) => setCustomProductPrice(e.target.value)}
          type="text"
          value={customProductPrice}
          min={0.01}
        />
        <TextField
          label="MPN"
          required
          onChange={(e) => setCustomProductMPN(e.target.value)}
          value={customProductMPN}
        />
      </ModalForm>
      <ModalForm
        open={addAltMpnModalOpen}
        onClose={addAltMpnModalToggler.off}
        title="Edit Alt Mpn"
        onSubmit={(e) => {
          e.preventDefault()

          if (activeLineItem) {
            addAltMpn(activeLineItem.product.id, altMpn)
              .then((_result) => {
                if (activeLineItem) {
                  const updatedLineItems = storeOrder.lineItems.map((lineItem) => {
                    if (lineItem.id === activeLineItem.id) {
                      return {
                        ...lineItem,
                        product: { ...lineItem.product, altMpn },
                      }
                    }
                    return lineItem
                  })
                  const updatedStoreOrder = {
                    ...storeOrder,
                    lineItems: updatedLineItems,
                  }
                  setStoreOrder(updatedStoreOrder)
                }

                msgsMgr.add('Alt MPN Updated!', 'positive')
              })
              .catch((err: GraphQLError) => {
                msgsMgr.add(err.message || UNKNOWN_ERROR, 'negative')
              })
              .finally(addAltMpnModalToggler.off)
          }
        }}
        submitButtonText="Update"
      >
        <TextField
          label="Alt Mpn"
          required
          defaultValue={activeLineItem?.product.altMpn || ''}
          onChange={(e) => setAltMpn(e.target.value)}
        />
      </ModalForm>
      <Page title="Edit Order" previousPage={`/orders/${storeOrderId}`}>
        <Layout>
          <Layout.Section type="primary">
            <Card
              title="Add Items"
              headerButton={
                <Button
                  primary={false}
                  type="button"
                  onClick={() => customProductModalToggler.on()}
                >
                  Add Custom Item
                </Button>
              }
              className={classNames({ 'pb-6': !store.hasCatalog })}
            >
              {store.hasCatalog && (
                <AddProductFilters
                  restrictToStoreId={storeOrder.store.id}
                  addProduct={addProduct}
                  newOrderLineItems={updatedOrderLineItems}
                  setProductsFromQuery={setProductsFromQuery}
                  productsFromQuery={productsFromQuery}
                />
              )}
            </Card>
            <Card title="Items in Order">
              <Card.Section>
                {updatedOrderLineItems.map((updateOrderLineItem, index) => {
                  const originalLineItem = storeOrder.lineItems.find(
                    (item) => item.id === updateOrderLineItem.id
                  )
                  return (
                    <Card.SubSection
                      key={updateOrderLineItem.id ?? `${updateOrderLineItem.product.id}-${index}`}
                    >
                      <OrderLineItem
                        pickup={pickup}
                        changedItem={updateOrderLineItem}
                        originalItem={originalLineItem}
                        onAdjustAvailability={() => {
                          prepAndOpenAvailabilityModal(updateOrderLineItem)
                        }}
                        onAdjustQuantity={() => {
                          prepAndOpenAdjustQuantityModal(updateOrderLineItem)
                        }}
                        onAdjustPrice={() => {
                          prepAndOpenAdjustUnitPriceModal(updateOrderLineItem)
                        }}
                        onRemove={() => {
                          removeAction(updateOrderLineItem)
                        }}
                        onEditAltMpn={() => {
                          prepAndOpenAltMpnModal(updateOrderLineItem)
                        }}
                      />
                    </Card.SubSection>
                  )
                })}
              </Card.Section>
              <Card.Section>
                <div className="space-y-6">
                  <Field
                    label="Expected Shipping/Lead Time (optional)"
                    desc={
                      <>
                        <span className="font-medium">Note:</span> This information is shared with
                        the customer.
                      </>
                    }
                  >
                    <TextArea
                      value={shippingLeadTime}
                      onChange={(e) => setShippingLeadTime(e.target.value)}
                    />
                  </Field>
                  <PickupField pickup={pickup} onChange={onPickupChange} />
                  {pickup && (
                    <div className="space-y-4">
                      <h4>Pickup Address</h4>
                      <AddressInput
                        hideFirstLastName
                        hideCompany
                        address={form.pickupAddress || undefined}
                        onChange={(pickupAddress) => setForm({ ...form, pickupAddress })}
                      />
                    </div>
                  )}
                </div>
              </Card.Section>
            </Card>
            <Card title="Payment">
              <Card.Section>
                <Card.SubSection>
                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span className="space-x-4">
                        <span>Subtotal</span>

                        {updatedOrderLineItemsCount !== storeOrder.lineItems.length && (
                          <span className="line-through text-gray-400">{`${storeOrder.lineItems.length} items`}</span>
                        )}

                        <span>{`${updatedOrderLineItemsCount} ${pluralize(
                          'items',
                          updatedOrderLineItemsCount
                        )}`}</span>
                      </span>

                      <span className="space-x-4">
                        {!MoneyM.equals(updatedSubtotal, subtotal) && (
                          <span className="line-through text-gray-400">
                            {MoneyM.format(subtotal)}
                          </span>
                        )}
                        <span>{MoneyM.format(updatedSubtotal)}</span>
                      </span>
                    </div>

                    {!pickup && (
                      <div className="flex justify-between">
                        <span className="space-x-4">
                          <span>Shipping</span>

                          {UserContext.isAdmin(user) && (
                            <LinkButton type="button" onClick={() => prepAndOpenShippingModal()}>
                              Adjust Shipping
                            </LinkButton>
                          )}
                        </span>

                        <span>
                          {shippingLoading ? (
                            <SpinnerSmall />
                          ) : (
                            <span className="space-x-4">
                              {shippingHasChanged && (
                                <span className="line-through text-gray-400">
                                  {MoneyM.format(storeOrder.shippingAmount)}
                                </span>
                              )}
                              <span>{MoneyM.format(updatedShipping)}</span>
                            </span>
                          )}
                        </span>
                      </div>
                    )}

                    {storeOrder.convenienceFeeRate > 0 && (
                      <div className="flex justify-between">
                        <span>Customer Fee</span>
                        <span className="space-x-4">
                          {!MoneyM.equals(updatedConvenienceFee, convenienceFee) && (
                            <span className="line-through text-gray-400">
                              {MoneyM.format(convenienceFee)}
                            </span>
                          )}
                          <span>{MoneyM.format(updatedConvenienceFee)}</span>
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span className="space-x-4">
                        <span>Sales Tax</span>
                        {UserContext.isAdmin(user) && (
                          <LinkButton type="button" onClick={() => prepAndOpenTaxModal()}>
                            Adjust Tax
                          </LinkButton>
                        )}
                      </span>
                      <span className="space-x-4">
                        {taxHasChanged && (
                          <span className="line-through text-gray-400">
                            {MoneyM.format(storeOrder.salesTax)}
                          </span>
                        )}
                        <span>{MoneyM.format(updatedTax)}</span>
                      </span>
                    </div>

                    {/* Charges added from the order page count toward the total, so show them
                        here too - otherwise they look deleted on this page (GEA-2793). */}
                    {storeOrder.additionalCharges.map((additionalCharge) => (
                      <div key={additionalCharge.id} className="flex justify-between">
                        <span>{additionalCharge.name}</span>
                        <span>{MoneyM.format(additionalCharge.price)}</span>
                      </div>
                    ))}

                    {UserContext.isAdmin(user) && (
                      <div className="flex justify-between">
                        <span className="space-x-4">
                          <span>Discount</span>
                          <LinkButton type="button" onClick={() => prepAndOpenDiscountModal()}>
                            Adjust Discount
                          </LinkButton>
                        </span>
                        <span className="space-x-4">
                          {discountHasChanged && (
                            <span className="line-through text-gray-400">
                              {originalDiscountPercent !== null
                                ? `${originalDiscountPercent}%`
                                : 'None'}
                            </span>
                          )}
                          <span>
                            {updatedDiscountPercent !== null
                              ? `${updatedDiscountPercent}%`
                              : 'None'}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </Card.SubSection>
                <Card.SubSection>
                  <div className="flex justify-between text-sm text-gray-700 font-bold">
                    <span>Total</span>
                    <span className="space-x-4">
                      {!MoneyM.equals(updatedTotal, total) && (
                        <span className="line-through text-gray-400">{MoneyM.format(total)}</span>
                      )}
                      <span>{MoneyM.format(updatedTotal)}</span>
                    </span>
                  </div>
                </Card.SubSection>
              </Card.Section>
            </Card>
          </Layout.Section>
          <Layout.Section type="secondary">
            <Card title="Summary">
              {!storeOrderHasChanged ? (
                <Card.Section>
                  <p className="text-sm text-gray-700">No changes have been made.</p>
                </Card.Section>
              ) : (
                <>
                  <Card.Section>
                    <ul className="ml-4 list-outside list-disc text-sm text-gray-700">
                      {Object.keys(changes).map((lineItemId) => {
                        const change = changes[lineItemId]
                        const originalLineItem = storeOrder.lineItems.find(
                          (item) => item.id === lineItemId
                        )
                        const addedToOrder = typeof originalLineItem === 'undefined'
                        return (
                          <Fragment key={lineItemId}>
                            {originalLineItem && originalLineItem?.inStock !== change.inStock && (
                              <li>Availability of {change.product.name} changed</li>
                            )}
                            {originalLineItem?.quantity !== change.quantity && (
                              <li>
                                {change.quantity === 0
                                  ? `${change.product.name} removed from order`
                                  : addedToOrder
                                    ? `Added ${change.product.name} at quantity ${
                                        change.quantity
                                      }, unit price ${MoneyM.format(change.unitPrice)}`
                                    : `Quantity of ${change.product.name} changed to ${change.quantity}`}
                              </li>
                            )}
                            {originalLineItem &&
                              change.quantity !== 0 &&
                              originalLineItem.unitPrice.amount !== change.unitPrice.amount && (
                                <li>
                                  Unit price of {change.product.name} changed to{' '}
                                  {MoneyM.format(change.unitPrice)}
                                </li>
                              )}
                          </Fragment>
                        )
                      })}
                      {pickupChanged && (
                        <li key="pickup-update">Delivery Method: {pickup ? 'Pickup' : 'Ship'}</li>
                      )}
                      {shippingHasChanged && (
                        <li key="shipping-update">
                          Shipping changed from {MoneyM.format(storeOrder.shippingAmount)} to{' '}
                          {MoneyM.format(updatedShipping)}
                        </li>
                      )}
                      {taxHasChanged && (
                        <li key="tax-update">
                          Sales Tax changed from {MoneyM.format(storeOrder.salesTax)} to{' '}
                          {MoneyM.format(updatedTax)}
                        </li>
                      )}
                      {discountHasChanged && (
                        <li key="discount-update">
                          Discount changed from{' '}
                          {originalDiscountPercent !== null
                            ? `${originalDiscountPercent}%`
                            : 'None'}{' '}
                          to{' '}
                          {updatedDiscountPercent !== null ? `${updatedDiscountPercent}%` : 'None'}
                        </li>
                      )}
                      {timingDetailsChanged && (
                        <li key="leadtime-update">
                          Shipping/Lead Time changed{' '}
                          {storeOrder.timingDetails ? `from ${storeOrder.timingDetails}` : ''} to{' '}
                          {shippingLeadTime}
                        </li>
                      )}
                      {pickupAddressChanged && (
                        <li key="pickup-address-update">Pickup address changed.</li>
                      )}
                    </ul>
                  </Card.Section>
                  {MoneyM.compare(updatedTotal, total) !== 0 && (
                    <Card.Section>
                      <p className="text-sm text-gray-700 font-bold flex justify-between">
                        <span>Quote change</span>
                        <span>
                          {MoneyM.toDecimal(MoneyM.sub(updatedTotal, total)) > 0 ? '+' : '-'}{' '}
                          {MoneyM.format(MoneyM.abs(MoneyM.sub(updatedTotal, total)))}
                        </span>
                      </p>
                    </Card.Section>
                  )}
                </>
              )}
              <Card.Section>
                {storeOrderHasChanged && (
                  <Button
                    type="button"
                    title="Discard Changes"
                    className="w-full justify-center mb-2"
                    primary={false}
                    onClick={() => {
                      setChanges({})
                      setUpdatedShipping(storeOrder.shippingAmount)
                      setUpdatedTax(storeOrder.salesTax)
                      setShippingLeadTime(storeOrder.timingDetails)
                      setPickup(storeOrder.pickup)
                      setUpdatedDiscountPercent(originalDiscountPercent)
                    }}
                  />
                )}
                <Button
                  type="button"
                  title="Update"
                  className="w-full justify-center"
                  disabled={!storeOrderHasChanged || shippingLoading}
                  onClick={onUpdateButtonClicked}
                />
              </Card.Section>
              {UserContext.isAdmin(user) && (
                <Card.Section
                  title="Convenience Fee"
                  headerButton={
                    <Button
                      primary={false}
                      type="button"
                      onClick={() => updateConvenienceModalToggler.on()}
                    >
                      Edit
                    </Button>
                  }
                >
                  <p className="text-sm text-gray-700">{storeOrder.convenienceFeeRate} %</p>
                </Card.Section>
              )}
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  )
}

export default EditOrder
